import { List, Map } from 'immutable'
import React from 'react'
import {
  AsyncState,
  StandardLocalWidget,
  Updater,
  Widget,
  any,
  async,
  fromJSX,
  loadingAsyncState,
  nothing
} from 'widgets-for-react'
import { renderBlock } from '../../cms/blocks/block_to_widget.gen'
import { BlockData, ImageSize } from '../../cms/blocks/types.gen'
import { blockToBlockData } from '../../drupal/drupal_to_cms.mod'
import { parseImageUrl } from '../../drupal/rendering/image_utils.mod'
import { CustomBlock } from '../../drupal/types_and_parser/custom_blocks.mod'
import { Block } from '../../drupal/types_and_parser/standard_blocks.gen'
import { ParsedFileImage, ParsedParagraphMediaNested } from '../../drupal/types_and_parser/types'
import { CustomBlockData, isCustomBlockData, renderCustomBlock } from '../blocks/custom_blocks/types.mod'
import { NotFoundPage } from '../templates/error_page.mod'
import { Loader } from '../templates/loader.mod'
import { DrupalPageLayout } from './layout'
import { DrupalPageState } from './state'

export const pageWidget =
  (departments: Array<string>, countries: Array<string>): StandardLocalWidget<DrupalPageState> =>
  (state) => {
    const page = state.page
    return any<Updater<DrupalPageState>>([
      state.page.kind == 'loaded' && state.page.value.node.bundle != 'homepage'
        ? async(state.page.value.breadcrumbs).map(
            (updater) =>
              (state): DrupalPageState =>
                state.page.kind == 'loaded'
                  ? {
                      ...state,
                      page: {
                        ...state.page,
                        value: { ...state.page.value, breadcrumbs: updater(state.page.value.breadcrumbs) }
                      }
                    }
                  : state
          )
        : nothing(),
      async(page).map((updater) => (state): DrupalPageState => {
        const newPage = updater(state.page)
        let images = state.images
        let blocks = state.blocks
        if (newPage.kind === 'loaded') {
          newPage.value.blocks.forEach((block) => {
            const imagesToLoad = getDrupalBlockImagesLoaders(block)
            imagesToLoad.forEach(([id, loader]) => {
              if (!images.has(id)) images = images.set(id, loader)
            })
          })
          blocks = Map<number, BlockData | CustomBlockData>(
            List(newPage.value.blocks).flatMap((block, index) => {
              let blockData = blockToBlockData(block, index)
              return blockData.kind === 'l' ? [] : [[index, blockData.v]]
            })
          )
        }
        return { ...state, page: newPage, blocks, images }
      }),
      ...state.images
        .map(
          (loader, id): Widget<Updater<DrupalPageState>> =>
            async(loader).map(
              (updater) =>
                (state): DrupalPageState => ({ ...state, images: state.images.set(id, updater(state.images.get(id))) })
            )
        )
        .valueSeq()
        .toArray(),

      page.kind == 'loaded'
        ? blocks(state).wrapHTML((blocksJSX) => (
            <DrupalPageLayout
              blocks={blocksJSX}
              page={page.value}
              breadcrumbs={page.value.breadcrumbs.kind == 'loaded' ? page.value.breadcrumbs.value : []}
              departments={departments}
              countries={countries}
            />
          ))
        : fromJSX(() => (page.kind == 'loading' ? <Loader /> : <NotFoundPage />))
    ])
  }

const getDrupalBlockImagesLoaders = (
  block: Block | CustomBlock
): Array<[number, AsyncState<Map<ImageSize, string>>]> => {
  switch (block.kind) {
    case 'text_with_image':
      return [
        [
          block.field_image &&
          block.field_image.bundle === 'image' &&
          block.field_image.field_media_image &&
          block.field_image.field_media_image.image.id
            ? block.field_image.field_media_image.image.id
            : null,
          loadImages(
            block.field_image &&
              block.field_image.bundle === 'image' &&
              block.field_image.field_media_image &&
              block.field_image.field_media_image.image.id
              ? block.field_image.field_media_image.image
              : null
          )
        ]
      ]
    case 'grid_blocks_image':
      return block.field_grid_blocks
        .map<[number, AsyncState<Map<ImageSize, string>>]>((b) => [
          b.field_image.field_media_image.image.id,
          loadImages(b.field_image.field_media_image.image)
        ])
        .toArray()
    case 'image_gallery':
      return block.field_image_gallery
        .map<[number, AsyncState<Map<ImageSize, string>>]>((b) => [
          b.field_image.field_media_image.image.id,
          loadImages(b.field_image.field_media_image.image)
        ])
        .toArray()
    case 'media':
      return [
        [
          block.field_media.bundle === 'image' ? block.field_media.field_media_image.image.id : null,
          loadImages(block.field_media.bundle === 'image' ? block.field_media.field_media_image.image : null)
        ]
      ]
    case 'cta':
      return [
        [
          block.field_image &&
          block.field_image.bundle === 'image' &&
          block.field_image.field_media_image &&
          block.field_image.field_media_image.image.id
            ? block.field_image.field_media_image.image.id
            : null,
          loadImages(
            block.field_image &&
              block.field_image.bundle === 'image' &&
              block.field_image.field_media_image &&
              block.field_image.field_media_image.image.id
              ? block.field_image.field_media_image.image
              : null
          )
        ]
      ]
    case 'text_with_read_more':
      const imagesTWRM: Array<[number, AsyncState<Map<ImageSize, string>>]> = []
      block.field_read_more
        .filter((frm) => frm.bundle === 'media_nested')
        .forEach((frm) => {
          const ppm = frm as ParsedParagraphMediaNested
          ppm.field_media_item.forEach((fmi) => {
            const imageId = fmi.field_media.bundle === 'image' ? fmi.field_media.field_media_image.image.id : null
            const imageData = fmi.field_media.bundle === 'image' ? fmi.field_media.field_media_image.image : null
            imagesTWRM.push([imageId, loadImages(imageData)])
          })
        })
      return imagesTWRM
    default:
      return []
  }
}

const loadImages = (image: ParsedFileImage): AsyncState<Map<ImageSize, string>> =>
  loadingAsyncState<Map<ImageSize, string>>(async () => {
    if (image == null) return { kind: 'result', status: 400, value: null }

    const sizes = image.style_urls

    for (const prop in sizes) {
      if (sizes[prop] == undefined)
        console.warn(`Error: missing image url. ${prop} is <undefined> for image with URL ${image.url}`)
    }

    let images = Map<ImageSize, string>().set('full', parseImageUrl(image.url))

    if (sizes.thumbnail) images = images.set('thumbnail', parseImageUrl(sizes.thumbnail))
    if (sizes.grid) images = images.set('grid', parseImageUrl(sizes.grid))
    if (sizes.media) images = images.set('media', parseImageUrl(sizes.media))
    if (sizes.large) images = images.set('large', parseImageUrl(sizes.large))
    if (sizes.medium) images = images.set('medium', parseImageUrl(sizes.medium))
    if (sizes.crop_thumbnail) images = images.set('crop_thumbnail', parseImageUrl(sizes.crop_thumbnail))
    if (sizes['720x470']) images = images.set('720x470', parseImageUrl(sizes['720x470']))
    if (sizes['640x360']) images = images.set('640x360', parseImageUrl(sizes['640x360']))
    if (sizes['1280x720']) images = images.set('1280x720', parseImageUrl(sizes['1280x720']))
    if (sizes['450x450']) images = images.set('450x450', parseImageUrl(sizes['450x450']))
    if (sizes['600x600']) images = images.set('600x600', parseImageUrl(sizes['600x600']))
    if (sizes['1200x1200']) images = images.set('1200x1200', parseImageUrl(sizes['1200x1200']))
    if (sizes['700w']) images = images.set('700w', parseImageUrl(sizes['700w']))
    if (sizes['1400w']) images = images.set('1400w', parseImageUrl(sizes['1400w']))
    if (sizes['2800w']) images = images.set('2800w', parseImageUrl(sizes['2800w']))
    if (sizes['425w']) images = images.set('425w', parseImageUrl(sizes['425w']))
    if (sizes['850w']) images = images.set('850w', parseImageUrl(sizes['850w']))
    if (sizes['1225w']) images = images.set('1225w', parseImageUrl(sizes['1225w']))
    if (sizes['640w']) images = images.set('640w', parseImageUrl(sizes['640w']))
    if (sizes['720w']) images = images.set('720w', parseImageUrl(sizes['720w']))
    if (sizes['1280w']) images = images.set('1280w', parseImageUrl(sizes['1280w']))
    if (sizes['1920w']) images = images.set('1920w', parseImageUrl(sizes['1920w']))
    if (sizes['2880w']) images = images.set('2880w', parseImageUrl(sizes['2880w']))
    return { kind: 'result', status: 200, value: images }
  })

const blocks: StandardLocalWidget<DrupalPageState> = (state) =>
  any(
    state.blocks
      .map((block, index) =>
        isCustomBlockData(block)
          ? renderCustomBlock(block)(state).map<Updater<DrupalPageState>>(
              (updater) =>
                (state): DrupalPageState => ({ ...state, ...updater(state) })
            )
          : renderBlock(
              block,
              `key-${index}`
            )(state).map<Updater<DrupalPageState>>(
              (updater) =>
                (state): DrupalPageState => ({ ...state, ...updater(state) })
            )
      )
      .toArray()
  )
